import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import React, { useContext, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import WebDatatable from 'app/shared/datatable/web/datatable';
import MobileDatatable from 'app/shared/datatable/mobile/datatable';
import ConfirmationDialog from 'app/shared/dialogs/confirmation';

import { setPageTitle } from 'store/actions/header';
import {
  addOneTransactionalToStore,
  loadTransactionalsToStore,
  removeManyTransactionalFromStore
} from 'store/actions/transactional';
import { Button } from 'app/shared/button';
import GmModal from 'app/shared/modal/modal';
import { OptionsModal } from 'app/shared/option-modal';
import { urls } from 'services/_url';
import styles from '../transactional.module.css';
import { PageContentWrapper } from 'app/layouts/wrapper/page-content';
import { GridColumn, GridRow } from 'app/layouts/grid';
import { Spacer } from 'app/layouts/generic';
import { TransactionalStatBar } from './statbar';
import { useTransactionalService } from 'hooks/mailing/transactional';
import useScreenSize from 'hooks/size';
import { processDuplicateCode, processDuplicateName } from 'app/shared/utils/mailing';
import { PermissionsContext } from 'contexts/permissions';
import { useMailingConfigService } from 'hooks/mailing/configuration';
import { useMailboxService } from 'hooks/users/mailbox';
import { filter_config } from './config/filter';
import { truncateText } from 'app/shared/utils/general';

const TransactionalEmailList = () => {
  const history = useNavigate();
  const dispatch = useDispatch();
  const { isMobile, isTablet } = useScreenSize();
  const { tenant_id, token, id: user_id } = useSelector((state) => state.user_data);
  const { is_mobile_view } = useSelector((state) => state.metadata);
  const transactionals_in_store = useSelector((state) => state.transactionals);
  const { permissions } = useContext(PermissionsContext);
  const { fetchMailboxes } = useMailboxService();
  const { fetchConfiguration } = useMailingConfigService();
  const {
    createTransactional,
    deleteTransactionals,
    fetchTransactionals,
    creationOptions,
    searchTransactionals
  } = useTransactionalService();

  const [transactionals, setTransactionals] = useState([]);
  const [total_transactionals, setTotalTransactionals] = useState(0);
  const [is_dkim_set, setIsDKIMSet] = useState(false);
  const [is_search_mode, setSearchMode] = useState(false);
  const [loading_data, setLoadingData] = useState(true);
  const [mailboxes, setMailboxes] = useState([]);
  const [show_creation_options, setShowCreationOptions] = useState(false);
  const [show_confirmation, setShowConfirmation] = useState(false);
  const [transactionals_to_delete, setTransactionalsToDelete] = useState([]);

  useEffect(() => {
    dispatch(setPageTitle([{ title: 'Transactional Emails', path: '.' }]));
    fetchConfiguration().then(({ configurations: [config] }) => {
      if (!config) return;

      const { dns } = config;
      setIsDKIMSet(() => dns && !!dns.length);
    });

    fetchMailboxes().then(({ mailboxes: [mailbox] }) => {
      const emails = mailbox ? mailbox.emails : [];
      setMailboxes(emails.map((email) => ({ label: email, value: email })));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setTransactionals(Object.values(transactionals_in_store));
  }, [transactionals_in_store]);

  const config = {
    actions: {
      bulk: [[{ label: 'Delete', value: 'delete' }]],
      single: () => [
        [
          { label: 'Clone', value: 'clone', hidden: !permissions['transactional:create'] },
          {
            label: 'Edit',
            value: 'open',
            hidden: !permissions['transactional:update']
          }
        ],
        [{ label: 'Delete', value: 'delete', hidden: !permissions['transactional:delete'] }]
      ]
    },
    allow_bulk_action: true,
    css: {},
    fields: [
      {
        title: 'Name',
        key: 'name',
        sortable: true,
        isTitle: true
      },
      {
        title: 'Code',
        key: 'code',
        sortable: true,
        isTagline: true
      },
      {
        title: 'Sender Email',
        key: 'sender_email',
        sortable: true,
        isTagline: true,
        formatter: (value) => {
          return truncateText(value, isTablet ? 25 : 35) || 'N/A';
        }
      },
      {
        title: 'Date created',
        key: 'created_on',
        sortable: true,
        formatter: (value) => new Date(value).toDateString(),
        isMetadata: true
      }
    ],
    is_search_mode,
    items: transactionals,
    num_of_rows: 10,
    search_key: 'name',
    search_text: ''
  };

  const handleClone = async (payload) => {
    const data = {
      body: payload.body,
      is_advanced: payload.is_advanced,
      retry_email: payload.retry_email,
      sender_email: payload.sender_email,
      sender_name: payload.sender_name,
      subject: payload.subject,
      template: payload.template,
      tenant_id: payload.tenant_id,
      code: processDuplicateCode(payload.code, 'COPY', '_'),
      name: processDuplicateName(payload.name, 'copy', '-')
    };

    toast.info('Cloning transactional');
    const { transactionals } = await searchTransactionals('code', {
      query_string: `keyword=${data.code}`
    });
    const existing_record = transactionals.find(
      (transactional) => transactional.code === data.code
    );
    if (existing_record && existing_record.id) return toast.error('Code already in use.');

    const transactional = await createTransactional({ data });

    if (transactional && transactional.id) {
      dispatch(addOneTransactionalToStore({ ...transactional }));
    }
  };

  const handleDelete = async (ids = []) => {
    const data = {
      options: {
        id: ids.join()
      }
    };
    const result = deleteTransactionals({ data });

    if (result) {
      dispatch(removeManyTransactionalFromStore(ids));
      toast.success('Deletion successful.');
    }
  };

  const handleDeletionConfirmation = (permitted) => {
    if (permitted) {
      handleDelete(transactionals_to_delete);
    }

    setTransactionalsToDelete([]);
    setShowConfirmation(false);
  };

  const handleDatatableAction = (payload) => {
    const { name, type, data } = payload;
    if (type === 'single') {
      switch (name) {
        case 'clone':
          handleClone(data);
          break;
        case 'delete':
          setTransactionalsToDelete([data.id]);
          setShowConfirmation(true);
          break;
        default:
          if (data.is_advanced) {
            window.location.href = `${urls.editor}/transactionals/${data.id}?t=${token}&tenant_id=${tenant_id}&user_id=${user_id}`;
            return;
          }
          history(`/editor/transactional/${data.id}`);
      }
    }

    if (type === 'bulk') {
      switch (name) {
        case 'delete':
          setTransactionalsToDelete(() => data.map((transactional) => transactional.id));
          setShowConfirmation(true);
          break;
        default:
      }
    }
  };

  const handleItemClick = (payload) => {
    const { id } = payload;
    history(`/transactionals/${id}`);
  };

  const handleDataRequest = async (
    page,
    population = 10,
    options = { filter_query: '', sort_query: '' }
  ) => {
    try {
      setLoadingData(true);
      const query = options.filter_query || '';
      const sort_query = options.sort_query || 'sort_by=-created_on';

      const { transactionals, size } = await fetchTransactionals({
        query_string: `${query}&page=${page}&population=${population}&${sort_query}`
      });
      setTotalTransactionals(size);
      dispatch(loadTransactionalsToStore(transactionals));
    } catch (e) {
      dispatch(loadTransactionalsToStore([]));
    } finally {
      setLoadingData(false);
    }
  };

  const handleSearchRequest = async (
    keys,
    keyword,
    page,
    population = 10,
    options = { filter_query: '', sort_query: '' }
  ) => {
    if (!keys) return;
    try {
      setLoadingData(true);
      const query = options.filter_query || '';
      const sort_query = options.sort_query || 'sort_by=-created_on';

      const { transactionals, size } = await searchTransactionals(keys, keyword, {
        query_string: `${query}&page=${page}&population=${population}&${sort_query}`
      });

      setTotalTransactionals(size);
      dispatch(loadTransactionalsToStore(transactionals));
    } catch (e) {
      dispatch(loadTransactionalsToStore([]));
    } finally {
      setLoadingData(false);
    }
  };

  const table_actions = (
    <>
      <Button
        icon_name="add"
        text="Create transactional"
        onClick={() => setShowCreationOptions(true)}
        disabled={!permissions['transactional:create']}
      />
    </>
  );

  return (
    <PageContentWrapper>
      {!isMobile && (
        <>
          <TransactionalStatBar />
          <Spacer multiple={4} />
        </>
      )}
      <GridRow>
        <GridColumn span={4}>
          {is_mobile_view ? (
            <MobileDatatable
              config={config}
              action={handleDatatableAction}
              onClick={handleItemClick}
              onListModeChange={setSearchMode}
              onDataRequest={handleDataRequest}
              onSearchRequest={handleSearchRequest}
              showHeader
            />
          ) : (
            <div>
              <WebDatatable
                config={{
                  ...config,
                  total_count: total_transactionals
                }}
                has_filter
                filter_config={filter_config(mailboxes)}
                action={handleDatatableAction}
                onClick={handleItemClick}
                checkbox
                loading_data={loading_data}
                table_actions={table_actions}
                onDataRequest={handleDataRequest}
                onSearchRequest={handleSearchRequest}
              />
            </div>
          )}
          <ConfirmationDialog
            type="error"
            title="Delete transactional email"
            message="Are you sure you want to delete this Transactional?"
            callback={handleDeletionConfirmation}
            is_open={show_confirmation}
          />
          <GmModal
            title="Choose creation mode"
            show_modal={show_creation_options}
            onClose={() => setShowCreationOptions(false)}
            bodyClassName={styles.optionsModal}
            show_title
          >
            <OptionsModal is_dkim_set={is_dkim_set} options={creationOptions} />
          </GmModal>
        </GridColumn>
      </GridRow>
    </PageContentWrapper>
  );
};

export default TransactionalEmailList;
